<template>
  <Listbox v-model="localModel" :open="isOpen" as="div" @blur="isOpen = false">
    <div class="flex gap-2">
      <ListboxLabel
        v-if="label"
        class="relative flex items-center gap-1 font-semibold leading-6 text-gray-900 md:font-bold"
      >
        <span>{{ label }}</span>
      </ListboxLabel>
      <!-- Don't add hover on mobile because it breaks safari compatability -->
      <div class="hidden md:block">
        <DefaultTooltip
          v-if="localModel.value?.description"
          ref="popper"
          class="group text-sm font-normal"
          @touchstart="openPopper"
        >
          <template #default>
            <InformationCircleIcon
              class="h-5 w-5 cursor-pointer text-gray group-hover:text-black"
            />
          </template>

          <template #content>
            <span v-html="localModel.value.description" />
          </template>
        </DefaultTooltip>
      </div>
      <div class="block md:hidden">
        <DefaultTooltip
          v-if="localModel.value?.description"
          ref="popper"
          class="group text-sm font-normal"
          @touchstart="openPopper"
        >
          <template #default>
            <InformationCircleIcon
              class="h-5 w-5 cursor-pointer text-gray group-hover:text-black"
            />
          </template>

          <template #content>
            <span v-html="localModel.value.description" />
          </template>
        </DefaultTooltip>
      </div>
    </div>
    <div class="relative mt-1">
      <ListboxButton
        ref="selectButton"
        :disabled="isLoading"
        class="relative w-full cursor-pointer rounded-md border border-gray-400 bg-white py-2 pl-4 pr-10 text-left sm:leading-6"
        @click="isOpen = !isOpen"
      >
        <span class="flex gap-4">
          <slot :value="localModel.value" name="button-label">
            {{ localModel.value?.label }}
          </slot>

          <div
            v-if="
              localModel.value?.predefinedShow && isShowingPredefinedShowText
            "
            class="flex items-center"
          >
            <CheckIcon class="h-5 w-5 text-order-buttons" />

            <span class="text-sm italic">
              {{ localModel?.value?.predefinedShowText }}
            </span>
          </div>
        </span>

        <span
          v-if="isLoading"
          class="absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <SpinnerIcon />
        </span>
        <span
          v-else
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronDownIcon
            :class="
              isOpen
                ? ['rotate-180', 'duration-200']
                : ['rotate-0', 'duration-200']
            "
            class="h-7 w-7 text-gray-600"
            aria-hidden="true"
          />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        @after-leave="isOpen = false"
      >
        <ListboxOptions
          ref="selectOptions"
          :class="{
            'bottom-full rounded-t-md border-b-0': showAbove,
            'rounded-b-md border-t-0': !showAbove,
          }"
          class="absolute z-10 mt-0 max-h-60 w-full overflow-auto border border-gray-400 bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
        >
          <ListboxOption
            v-for="option in options"
            :key="option.value"
            v-slot="{ active }"
            :value="option"
            as="template"
            @click="isOpen = false"
          >
            <li
              :class="[
                active
                  ? 'cursor-pointer bg-gray-lightest/30 text-gray-900'
                  : 'cursor-pointer text-gray-900',
                option.value === localModel.value.value
                  ? 'bg-gray-lightest/40'
                  : '',
              ]"
              class="relative flex select-none border-b-2 border-gray-lightest py-2 pl-3 pr-9 last:border-b-0"
            >
              <span
                :class="[
                  option.value === localModel.value.value
                    ? 'font-semibold'
                    : 'font-normal',
                ]"
                class="flex gap-1 truncate"
              >
                {{ option.label }}
              </span>

              <span
                v-if="showPrices"
                class="absolute inset-y-0 right-0 flex items-center pr-4"
              >
                <label
                  class="ml-2 mr-3 flex w-full cursor-pointer items-center justify-between leading-6"
                >
                  <div class="flex items-center justify-end gap-3">
                    <span
                      class="text-gray-lighter line-through"
                      v-html="option.localizedRecommendedPrice"
                    />
                    <span
                      class="font-semibold"
                      v-html="option.localizedPrice"
                    />
                  </div>
                </label>
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue';
import {
  CheckIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/vue/20/solid';
import { computed, ComputedRef, ModelRef, PropType, Ref, ref } from 'vue';
import Popper from 'vue3-popper';
import { useFilterStore } from '@/stores/Configurator/FilterStore';
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';
import DefaultTooltip from '@/components/Tooltips/DefaultTooltip.vue';

const emit = defineEmits(['update:model-value']);

const isOpen = ref(false);
const popper: Ref<typeof Popper | null> = ref(null);

const filterStore = useFilterStore();

const props = defineProps({
  index: {
    type: String,
    required: false,
    default: undefined,
  },

  label: {
    type: String,
    required: false,
    default: undefined,
  },

  options: {
    type: Array as PropType<SelectorOption[]>,
    required: true,
  },

  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },

  showPrices: {
    type: Boolean,
    required: false,
    default: true,
  },

  showAbove: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const localModel = computed({
  get() {
    return model;
  },
  set(newValue) {
    emit('update:model-value', newValue);
  },
});

const model: ModelRef<SelectorOption> = defineModel({
  type: Object as PropType<SelectorOption>,
  required: true,
});
const isShowingPredefinedShowText: ComputedRef<boolean> = computed(
  () => filterStore.defaultFilterSkus[props.index ?? 0] === model.value.value
);

const selectButton: Ref<HTMLButtonElement | null> = ref(null);
const selectOptions: Ref<HTMLUListElement | null> = ref(null);

function openPopper() {
  popper.value?.$el.click();
}
</script>
